/**
 * App.ts — Root shell composition.
 *
 * Assembles TopBar, ModuleViewport, and BottomNav into the app layout.
 * Listens to router changes and swaps the active module in the viewport.
 */

import type { AweniContext } from './context/AweniContext';
import type { Router } from './router';
import { mountTopBar } from './shell/TopBar';
import { mountBottomNav, setActiveTab } from './shell/BottomNav';
import { mountModuleViewport, setActiveModule } from './shell/ModuleViewport';
import { mountNotificationDrawer, openNotificationDrawer } from './shell/NotificationDrawer';
import { mountProfileSwitcher, toggleProfileSwitcher } from './shell/ProfileSwitcher';
import { getModuleById, getBottomNavModules } from './modules/registry';
import { mountFooter } from './shell/Footer';

/**
 * Mount the full app shell into the given container.
 */
export function mountApp(root: HTMLElement, ctx: AweniContext, router: Router): void {
  root.innerHTML = '';

  // Accessibility skip link
  const skipLink = document.createElement('a');
  skipLink.href = '#aweni-main';
  skipLink.className = 'skip-link';
  skipLink.textContent = ctx.i18n.t('skip_to_content') || 'Skip to content';
  root.appendChild(skipLink);

  // Top bar
  const topBarEl = document.createElement('header');
  topBarEl.id = 'aweni-topbar';
  root.appendChild(topBarEl);

  // Notification drawer (off-screen overlay)
  const drawerEl = document.createElement('aside');
  drawerEl.id = 'aweni-notifications';
  root.appendChild(drawerEl);

  // Profile switcher (off-screen overlay)
  const profileEl = document.createElement('aside');
  profileEl.id = 'aweni-profile-switcher';
  root.appendChild(profileEl);

  // Module viewport (main content area)
  const viewportEl = document.createElement('main');
  viewportEl.id = 'aweni-main';
  viewportEl.setAttribute('role', 'main');
  viewportEl.className = 'module-viewport';
  viewportEl.style.cssText = 'flex: 1; overflow-y: auto; overflow-x: hidden;';
  root.appendChild(viewportEl);

  // Footer — sits OUTSIDE the scrollable viewport, between viewport and
  // bottom nav. (When it was mounted inside viewport it appeared at the TOP
  // of the page in DOM-order block flow because module containers are appended
  // after it. See feedback memory; reverted here.)
  const footerEl = document.createElement('footer');
  footerEl.id = 'aweni-footer';
  root.appendChild(footerEl);
  mountFooter(footerEl, ctx);

  // Bottom nav
  const bottomNavEl = document.createElement('nav');
  bottomNavEl.id = 'aweni-bottomnav';
  root.appendChild(bottomNavEl);

  // Mount shell components
  mountTopBar(topBarEl, ctx, {
    onNotificationsClick: () => openNotificationDrawer(),
    onProfileClick: () => toggleProfileSwitcher(),
  });

  mountNotificationDrawer(drawerEl, ctx);
  mountProfileSwitcher(profileEl, ctx);
  mountModuleViewport(viewportEl, ctx);

  const navModules = getBottomNavModules();
  mountBottomNav(bottomNavEl, ctx, navModules, (moduleId: string) => {
    router.navigate(`/${moduleId}`);
  });

  // Auth routes that should render full-screen (no shell chrome)
  const FULL_SCREEN_MODULES = new Set(['auth']);

  // React to route changes
  router.onChange((route) => {
    const moduleId = route.module || 'home';
    // For the "more" route without a sub-module, do nothing (popup handles it)
    if (moduleId === 'more') return;

    // Full-screen mode for auth (hide shell chrome)
    if (FULL_SCREEN_MODULES.has(moduleId)) {
      topBarEl.style.display = 'none';
      bottomNavEl.style.display = 'none';
      viewportEl.style.paddingTop = '0';
      viewportEl.style.paddingBottom = '0';
      footerEl.style.display = 'none';
      // Reset footer position when moving back from auth (footer lives
      // outside viewport now, see footer construction above).
    } else {
      topBarEl.style.display = '';
      bottomNavEl.style.display = '';
      viewportEl.style.paddingTop = '';
      viewportEl.style.paddingBottom = '';
      footerEl.style.display = '';
    }

    const moduleDef = getModuleById(moduleId);
    if (moduleDef) {
      setActiveModule(moduleId, ctx, route.subpath);
      setActiveTab(moduleId);
    }
  });

  // Navigate to current hash path or default to home / entry.
  // Unauthenticated users land on the 3-button entry screen (Guest / Login / Sign up).
  const hash = window.location.hash.replace(/^#/, '');
  const isAuthRoute = /^\/(entry|guest|claim|login|signup|forgot-password|reset-password|verify-email)\b/.test(hash);
  let initialPath: string;
  if (hash) {
    initialPath = hash;
  } else if (ctx.auth.isAuthenticated) {
    initialPath = '/home';
  } else {
    initialPath = '/entry';
  }
  // Edge case: explicit /home or empty for an unauthenticated user → entry
  if (!ctx.auth.isAuthenticated && !isAuthRoute && (hash === '' || /^\/home\b/.test(hash))) {
    initialPath = '/entry';
  }
  router.navigate(initialPath);
}
